import {
  BSCol,
  BSGrid,
  Button,
  Card,
  Select,
  SelectType
} from "@appquality/appquality-design-system";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
  useGetCampaignsByCampaignBugTypesQuery,
  useGetCampaignsByCampaignReplicabilitiesQuery,
  useGetCampaignsByCampaignSeveritiesQuery,
  useGetUsersMeCampaignsByCampaignIdBugsQuery
} from "src/services/tryberApi";
import {
  setSelectedBugType,
  setSelectedReplicability,
  setSelectedSeverity,
  setSelectedStatus
} from "../../redux/bugShow/actionCreator";
import { useAppDispatch } from "../../redux/provider";
import { FiltersToolbar } from "./styled";

interface BugShowFiltersProps {
  campaignId: string;
}

const BugShowFilters = ({
  campaignId,
}: BugShowFiltersProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const allLabel = t("All");

  const { data: severityData } = useGetCampaignsByCampaignSeveritiesQuery(
    { campaign: campaignId || "" },
    { skip: !campaignId }
  );

  const { data: replicabilityData } =
    useGetCampaignsByCampaignReplicabilitiesQuery(
      { campaign: campaignId || "" },
      { skip: !campaignId }
    );

  const { data: typeData } = useGetCampaignsByCampaignBugTypesQuery(
    { campaign: campaignId || "" },
    { skip: !campaignId }
  );

  const { data: bugsData } = useGetUsersMeCampaignsByCampaignIdBugsQuery(
    { campaignId: campaignId || "", limit: 100, start: 0 },
    { skip: !campaignId }
  );

  const {
    selectedSeverity,
    selectedStatus,
    selectedReplicability,
    selectedBugType,
  } = useSelector((state: GeneralState) => state.bugShow);

  if (!bugsData) return null;

  const status = Array.from(
    new Map(
      bugsData.items.map((bug) => [
        bug.status.id,
        { name: bug.status.name, id: bug.status.id },
      ])
    ).values()
  ).map((status) => ({
    label: status.name,
    value: status.id.toString(),
  }));
  const severities = severityData
    ? severityData.map((s) => ({
        label: s.name,
        value: s.id.toString(),
      }))
    : [];

  const replicabilities = replicabilityData
    ? replicabilityData.map((r) => ({
        label: r.name,
        value: r.id.toString(),
      }))
    : [];

  const bugTypes = typeData
    ? typeData.map((b) => ({
        label: b.name,
        value: b.id.toString(),
      }))
    : [];

  const severityValue: SelectType.Option =
    selectedSeverity &&
    severities &&
    severities.map((s) => s.value).includes(selectedSeverity.value || "")
      ? selectedSeverity
      : { label: allLabel };

  const statusValue: SelectType.Option =
    selectedStatus && status.map((s) => s.value).includes(selectedStatus.value || "")
      ? selectedStatus
      : { label: allLabel };

  const replicabilityValue: SelectType.Option =
    selectedReplicability &&
    replicabilities
      .map((r) => r.value)
      .includes(selectedReplicability.value || "")
      ? selectedReplicability
      : { label: allLabel };

  const bugTypeValue: SelectType.Option =
    selectedBugType &&
    bugTypes.map((b) => b.value).includes(selectedBugType.value || "")
      ? selectedBugType
      : { label: allLabel };

  const hasActiveFilters =
    !!severityValue.value ||
    !!statusValue.value ||
    !!replicabilityValue.value ||
    !!bugTypeValue.value;

  const resetFilters = () => {
    dispatch(setSelectedSeverity(undefined));
    dispatch(setSelectedStatus(undefined));
    dispatch(setSelectedReplicability(undefined));
    dispatch(setSelectedBugType(undefined));
  };

  return (
    <Card className="aq-mb-3" bodyClass="aq-p-3" shadow>
      <BSGrid>
        <BSCol size="col-lg-3 col-md-6">
          <Select
            label={t("Severity")}
            onChange={(value) => dispatch(setSelectedSeverity(value))}
            name="severity"
            options={[{ label: allLabel }, ...severities]}
            value={severityValue}
            isSearchable={false}
            placeholder={t("Search")}
            isClearable={false}
            noOptionsMessage={() => t("__SELECT_DEFAULT_NO_OPTION")}
          />
        </BSCol>
        <BSCol size="col-lg-3 col-md-6">
          <Select
            label={t("Status")}
            onChange={(value) => dispatch(setSelectedStatus(value))}
            name="status"
            options={[{ label: allLabel }, ...status]}
            value={statusValue}
            isSearchable={false}
            placeholder={t("Search")}
            isClearable={false}
            noOptionsMessage={() => t("__SELECT_DEFAULT_NO_OPTION")}
          />
        </BSCol>
        <BSCol size="col-lg-3 col-md-6">
          <Select
            label={t("Replicability")}
            onChange={(value) => dispatch(setSelectedReplicability(value))}
            name="replicability"
            options={[{ label: allLabel }, ...replicabilities]}
            value={replicabilityValue}
            isSearchable={false}
            placeholder={t("Search")}
            isClearable={false}
            noOptionsMessage={() => t("__SELECT_DEFAULT_NO_OPTION")}
          />
        </BSCol>
        <BSCol size="col-lg-3 col-md-6">
          <Select
            label={t("Bug Type")}
            onChange={(value) => dispatch(setSelectedBugType(value))}
            name="bugType"
            options={[{ label: allLabel }, ...bugTypes]}
            value={bugTypeValue}
            isSearchable={false}
            placeholder={t("Search")}
            isClearable={false}
            noOptionsMessage={() => t("__SELECT_DEFAULT_NO_OPTION")}
          />
        </BSCol>
      </BSGrid>
      {hasActiveFilters && (
        <FiltersToolbar>
          <Button kind="link-hover" size="sm" onClick={resetFilters}>
            {t("Reset filters")}
          </Button>
        </FiltersToolbar>
      )}
    </Card>
  );
};

export default BugShowFilters;
